import { useQuery } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';

export type GradeConversionPagePropsResponse = {
  referenceData: {
    pageSizes: number[];
    countries: Record<string, { alpha3Code: string; countryLabel: string; nationalityLabel: string }>;
    educationLevels: Record<string, { name: string; code: string }>;
    courses: Record<string, { name: string; code: string }>;
  };
  initialQueryParameters: {
    pageSize: number;
    sort: string;
  };
};

export type ReferenceData = GradeConversionPagePropsResponse['referenceData'];
export type InitialQueryParameter = GradeConversionPagePropsResponse['initialQueryParameters'];

export type GradeConversionPageInitialProps = {
  referenceData: ReferenceData;
  initialQueryParameters: InitialQueryParameter;
};

export function useGradeConversionPageProps() {
  const request = useRequest();
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ['useGradeConversionPageProps'],
    queryFn: async () => {
      const response = await request<GradeConversionPagePropsResponse>(
        `${config.apiHost}/private/v1/grade_conversion/page_props`,
        { isExpectedResponse, method: 'GET' }
      );

      const sortValues = response.initialQueryParameters.sort.split(',');
      const sort = sortValues.length > 0 ? sortValues[0] : 'countryCode';

      return {
        referenceData: response.referenceData,
        initialQueryParameters: { sort, pageSize: response.initialQueryParameters.pageSize },
      };
    },
  });
  return {
    isInitialPropsError: isError,
    initialPropsError: error,
    isLoadingInitialProps: isLoading,
    initialProps: data,
  };
}

function isExpectedResponse(res: unknown): res is GradeConversionPagePropsResponse {
  return Boolean(res && typeof res === 'object');
}
